import apiSlice from "../api/apiSlice";

const managedVersionsPath = ({ entityType, entityId }) => {
  const [primaryId, secondaryId] = decodeURIComponent(entityId).split("~");
  const paths = {
    config: "config/versions",
    membership: `memberships/${primaryId}/versions`,
    meter: `meters/${primaryId}/versions`,
    resource: `resources/${primaryId}/versions`,
    ownership: `ownerships/${primaryId}/${secondaryId}/versions`,
    contract: `meters/${primaryId}/contracts/versions`,
    resp: `resp/${primaryId}/versions`,
    fixedBilateral: `fixed-bilateral/${primaryId}/${secondaryId}/versions`,
    ems: `ems/${primaryId}/versions`,
  };
  return paths[entityType];
};

const deletedVersionCacheTags = ({ entityType, entityId }) => {
  const tags = [{ type: "BusinessValidity", id: `${entityType}:${entityId}` }];
  if (entityType === "resource") {
    const [resourceId] = decodeURIComponent(entityId).split("~");
    tags.push({ type: "Resource", id: "LIST" }, { type: "Resource", id: resourceId });
  }
  return tags;
};

export const businessValidityApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getManagedVersions: builder.query({
      query: ({ communityId, entityType, entityId }) => {
        const path = managedVersionsPath({ entityType, entityId });
        if (!path) throw new Error(`Unsupported business-valid entity: ${entityType}`);
        return `/communities/${communityId}/business-validity/${path}`;
      },
      providesTags: (_, __, { entityType, entityId }) => [
        { type: "BusinessValidity", id: `${entityType}:${entityId}` },
      ],
    }),
    deleteManagedVersion: builder.mutation({
      query: ({ communityId, entityType, entityId, versionId }) => ({
        url: `/communities/${communityId}/business-validity/managed/${entityType}/${encodeURIComponent(
          entityId
        )}/versions/${versionId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_, __, argument) => deletedVersionCacheTags(argument),
    }),
    getBusinessSnapshot: builder.query({
      query: ({ communityId, effectiveAt }) => ({
        url: `/communities/${communityId}/business-validity/snapshot?effective_at=${encodeURIComponent(
          effectiveAt
        )}`,
        method: "GET",
      }),
      providesTags: (_, __, { communityId, effectiveAt }) => [
        { type: "BusinessValidity", id: `${communityId}:snapshot:${effectiveAt}` },
      ],
    }),
    getConfigVersions: builder.query({
      query: (communityId) => `/communities/${communityId}/business-validity/config/versions`,
    }),
    getMembershipVersions: builder.query({
      query: ({ communityId, userId }) =>
        `/communities/${communityId}/business-validity/memberships/${userId}/versions`,
    }),
    getMeterVersions: builder.query({
      query: ({ communityId, meterId }) =>
        `/communities/${communityId}/business-validity/meters/${meterId}/versions`,
    }),
    getResourceVersions: builder.query({
      query: ({ communityId, resourceId }) =>
        `/communities/${communityId}/business-validity/resources/${resourceId}/versions`,
    }),
    getOwnershipVersions: builder.query({
      query: ({ communityId, resourceId, userId }) =>
        `/communities/${communityId}/business-validity/ownerships/${resourceId}/${userId}/versions`,
    }),
    getContractVersions: builder.query({
      query: ({ communityId, meterId }) =>
        `/communities/${communityId}/business-validity/meters/${meterId}/contracts/versions`,
    }),
    getRespVersions: builder.query({
      query: ({ communityId, respId }) =>
        `/communities/${communityId}/business-validity/resp/${respId}/versions`,
    }),
    getFixedBilateralVersions: builder.query({
      query: ({ communityId, receiverUserId, providerUserId }) =>
        `/communities/${communityId}/business-validity/fixed-bilateral/${receiverUserId}/${providerUserId}/versions`,
    }),
    getEmsConfigurationVersions: builder.query({
      query: ({ communityId, configId }) =>
        `/communities/${communityId}/business-validity/ems/${configId}/versions`,
    }),
    importBusinessValidity: builder.mutation({
      query: ({ communityId, payload, legacyBaseEffectiveDate }) => ({
        url:
          `/communities/${communityId}/business-validity/import` +
          `${
            legacyBaseEffectiveDate
              ? `?legacy_base_effective_date=${encodeURIComponent(legacyBaseEffectiveDate)}`
              : ""
          }`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: [{ type: "BusinessValidity", id: "LIST" }],
    }),
  }),
});

export const {
  useGetManagedVersionsQuery,
  useDeleteManagedVersionMutation,
  useGetBusinessSnapshotQuery,
  useGetConfigVersionsQuery,
  useGetMembershipVersionsQuery,
  useGetMeterVersionsQuery,
  useGetResourceVersionsQuery,
  useGetOwnershipVersionsQuery,
  useGetContractVersionsQuery,
  useGetRespVersionsQuery,
  useGetFixedBilateralVersionsQuery,
  useGetEmsConfigurationVersionsQuery,
  useImportBusinessValidityMutation,
} = businessValidityApiSlice;
